import React, { useState, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  VStack,
  Heading,
  IconButton,
  HStack,
  Divider,
  Tooltip,
  Collapse,
  Text,
} from "@chakra-ui/react";
import {
  FiLogOut,
  FiTool,
  FiFileText,
  FiUser,
  FiHome,
  FiChevronLeft,
  FiChevronRight,
  FiColumns,
} from "react-icons/fi";
import QuickLinksPanel from "./QuickLinksPanel";
import NotificationBell from "./NotificationBell";

const hoverScale1 = { transform: "scale(1)" };

const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [ordersExpanded, setOrdersExpanded] = useState(false);

  const isActive = (path) => {
    if (path === "/tools") return location.pathname.startsWith("/tools");
    return location.pathname === path;
  };

  const navItemsTop = useMemo(
    () => [
      { label: "Dashboard (WIP)", icon: FiHome, path: "/dashboard" },
      { label: "Tools", icon: FiTool, path: "/tools" },
      { label: "IVR Status", icon: FiFileText, path: "/ivrstatus" },
    ],
    []
  );

  const navItemsBottom = useMemo(
    () => [
      { label: "Invoices", icon: FiFileText, path: "/invoices" },
      { label: "Kanban", icon: FiColumns, path: "/tools/kanban" },
      { label: "Account (WIP)", icon: FiUser, path: "/account" },
      { label: "Rep Dashboard (WIP)", icon: FiFileText, path: "/rep-dashboard" },
      { label: "Products", icon: FiColumns, path: "/tools/products" },
      { label: "Admin Logs", icon: FiFileText, path: "/tools/admin-logs" },
    ],
    []
  );

  return (
    <Box
      w={isCollapsed ? "80px" : "250px"}
      bg="gray.800"
      color="white"
      px={isCollapsed ? 2 : 4}
      py={6}
      transition="width 0.2s ease-in-out"
      overflow="hidden"
      borderRight="1px solid"
      borderColor="gray.700"
    >
      {/* Header: Title (expanded), Bell, Collapse toggle */}
      <HStack justifyContent="space-between" mb={6} minH="40px" spacing={2}>
        <Box whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis">
          {!isCollapsed && (
            <Heading size="md" color="white">
              Admin Portal
            </Heading>
          )}
        </Box>
        <HStack spacing={1}>
          <NotificationBell onClick={() => navigate("/notifications")} />
          <Tooltip label={isCollapsed ? "Expand" : "Collapse"} placement="right">
            <IconButton
              icon={isCollapsed ? <FiChevronRight /> : <FiChevronLeft />}
              variant="ghost"
              colorScheme="whiteAlpha"
              onClick={() => setIsCollapsed(!isCollapsed)}
              size="sm"
              aria-label="Toggle sidebar"
              _hover={hoverScale1}
            />
          </Tooltip>
        </HStack>
      </HStack>

      <Divider borderColor="gray.600" mb={6} />

      <VStack align="stretch" spacing={2}>
        {/* Top items */}
        {navItemsTop.map((item) => (
          <Tooltip key={item.label} label={isCollapsed ? item.label : ""} placement="right">
            <Button
              leftIcon={<item.icon />}
              justifyContent={isCollapsed ? "center" : "flex-start"}
              variant="ghost"
              colorScheme="whiteAlpha"
              bg={isActive(item.path) ? "gray.700" : "transparent"}
              onClick={() => navigate(item.path)}
              whiteSpace="nowrap"
              overflow="hidden"
              textOverflow="ellipsis"
              _hover={{ bg: "gray.700", ...hoverScale1 }}
              px={isCollapsed ? 0 : 3}
            >
              {!isCollapsed && item.label}
            </Button>
          </Tooltip>
        ))}

        {/* Orders (only expandable when expanded) */}
        <Box>
          <Tooltip label={isCollapsed ? "Orders" : ""} placement="right">
            <Button
              leftIcon={<FiFileText />}
              justifyContent={isCollapsed ? "center" : "flex-start"}
              variant="ghost"
              colorScheme="whiteAlpha"
              bg={
                isActive("/orders") || location.pathname.includes("/admin/treatment-orders")
                  ? "gray.700"
                  : "transparent"
              }
              onClick={() => {
                if (isCollapsed) {
                  navigate("/orders");
                } else {
                  setOrdersExpanded((v) => !v);
                }
              }}
              width="100%"
              whiteSpace="nowrap"
              overflow="hidden"
              textOverflow="ellipsis"
              _hover={{ bg: "gray.700", ...hoverScale1 }}
              px={isCollapsed ? 0 : 3}
            >
              {!isCollapsed && "Orders"}
            </Button>
          </Tooltip>

          {/* Submenu appears only when expanded sidebar */}
          {!isCollapsed && (
            <Collapse in={ordersExpanded} animateOpacity>
              <VStack align="stretch" spacing={1} pl={6} pt={2}>
                <Button
                  variant="ghost"
                  colorScheme="whiteAlpha"
                  size="sm"
                  bg={
                    location.pathname === "/orders" && location.search.includes("type=graft")
                      ? "gray.600"
                      : "transparent"
                  }
                  onClick={() => navigate("/orders?type=graft")}
                  fontSize="sm"
                  _hover={{ bg: "gray.600", ...hoverScale1 }}
                >
                  Graft
                </Button>
                <Button
                  variant="ghost"
                  colorScheme="whiteAlpha"
                  size="sm"
                  bg={
                    location.pathname.includes("/admin/treatment-orders")
                      ? "gray.600"
                      : "transparent"
                  }
                  onClick={() => navigate("/admin/treatment-orders")}
                  fontSize="sm"
                  _hover={{ bg: "gray.600", ...hoverScale1 }}
                >
                  DME
                </Button>
              </VStack>
            </Collapse>
          )}
        </Box>

        {/* Bottom items */}
        {navItemsBottom.map((item) => (
          <Tooltip key={item.label} label={isCollapsed ? item.label : ""} placement="right">
            <Button
              leftIcon={<item.icon />}
              justifyContent={isCollapsed ? "center" : "flex-start"}
              variant="ghost"
              colorScheme="whiteAlpha"
              bg={isActive(item.path) ? "gray.700" : "transparent"}
              onClick={() => navigate(item.path)}
              whiteSpace="nowrap"
              overflow="hidden"
              textOverflow="ellipsis"
              _hover={{ bg: "gray.700", ...hoverScale1 }}
              px={isCollapsed ? 0 : 3}
            >
              {!isCollapsed && item.label}
            </Button>
          </Tooltip>
        ))}

        <Divider borderColor="gray.600" my={4} />

        {/* Logout */}
        <Tooltip label={isCollapsed ? "Logout" : ""} placement="right">
          <Button
            leftIcon={<FiLogOut />}
            bg="red.500"
            color="white"
            justifyContent={isCollapsed ? "center" : "flex-start"}
            _hover={{ bg: "red.600", ...hoverScale1 }}
            onClick={() => navigate("/logout")}
            px={isCollapsed ? 0 : 3}
          >
            {!isCollapsed && "Logout"}
          </Button>
        </Tooltip>
      </VStack>

      {/* Keep quick links panel only in expanded mode to avoid cramped UI */}
      {!isCollapsed && <QuickLinksPanel />}
    </Box>
  );
};

export default Sidebar;
